/**
 * Publishing channels and the hard rules each platform enforces. One table, used by the planner
 * (what to draft), guardrails (what to block) and the publisher (where to send it).
 */

import { CHANNELS, type Channel } from '@marketingbee/contracts';

export { CHANNELS, type Channel };

/** Which connection publishes a channel. `email` uses the Bee's own sender; `blog` is exported. */
export const CHANNEL_CONNECTION = {
  instagram: 'konekto_post',
  facebook: 'konekto_post',
  linkedin: 'konekto_post',
  bluesky: 'bluesky',
  email: 'email',
  blog: 'export',
} as const satisfies Record<Channel, 'konekto_post' | 'bluesky' | 'email' | 'export'>;

export type ChannelRules = {
  label: string;
  /** Hard platform limit for the body (characters; Bluesky counts graphemes). */
  maxLength: number;
  /** Body length we aim for when drafting. */
  idealLength: [number, number];
  /** The platform refuses a post without an image or video. */
  requiresMedia: boolean;
  /** Links in the body are clickable. Instagram's are not ("link in bio"). */
  clickableLinks: boolean;
  maxHashtags: number | null;
  /** Needs a subject line (email) or title (blog). */
  needsTitle: boolean;
  /** Social posts count toward the plan's monthly post limit; email and blog do not. */
  countsAsPost: boolean;
};

export const CHANNEL_RULES: Readonly<Record<Channel, ChannelRules>> = {
  instagram: { label: 'Instagram', maxLength: 2200, idealLength: [120, 900], requiresMedia: true, clickableLinks: false, maxHashtags: 30, needsTitle: false, countsAsPost: true },
  facebook: { label: 'Facebook', maxLength: 63206, idealLength: [80, 600], requiresMedia: false, clickableLinks: true, maxHashtags: null, needsTitle: false, countsAsPost: true },
  linkedin: { label: 'LinkedIn', maxLength: 3000, idealLength: [300, 1300], requiresMedia: false, clickableLinks: true, maxHashtags: null, needsTitle: false, countsAsPost: true },
  bluesky: { label: 'Bluesky', maxLength: 300, idealLength: [80, 280], requiresMedia: false, clickableLinks: true, maxHashtags: null, needsTitle: false, countsAsPost: true },
  email: { label: 'Email', maxLength: 20000, idealLength: [400, 2500], requiresMedia: false, clickableLinks: true, maxHashtags: 0, needsTitle: true, countsAsPost: false },
  blog: { label: 'Blog post', maxLength: 40000, idealLength: [4000, 9000], requiresMedia: false, clickableLinks: true, maxHashtags: 0, needsTitle: true, countsAsPost: false },
};

export const SOCIAL_CHANNELS = CHANNELS.filter((c) => CHANNEL_RULES[c].countsAsPost);

export function isChannel(value: unknown): value is Channel {
  return typeof value === 'string' && (CHANNELS as readonly string[]).includes(value);
}

/** Length the way the platform counts it (Bluesky: graphemes; elsewhere UTF-16 code units). */
export function platformLength(channel: Channel, text: string): number {
  if (channel !== 'bluesky') return text.length;
  const Seg = (Intl as unknown as { Segmenter?: new (l?: string, o?: { granularity: 'grapheme' }) => { segment(s: string): Iterable<unknown> } }).Segmenter;
  if (!Seg) return [...text].length;
  let n = 0;
  for (const _ of new Seg(undefined, { granularity: 'grapheme' }).segment(text)) n++;
  return n;
}

export function countHashtags(text: string): number {
  return (text.match(/(^|\s)#[\p{L}\p{N}_]+/gu) ?? []).length;
}
